import React, { useEffect, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Table, Tag, Modal, Button, message, Dropdown } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { MoreOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { SeoHead } from 'modules/seo';
import { Divider } from 'modules/ui';
import { TableSearch } from 'modules/table';
import { Input } from 'modules/form';
import config from 'config';

interface ErrorLog {
  _id: string;
  timestamp: string;
  documentName: string;
  documentType: number;
  messageAi: string | null;
  fileName: string | null;
  response: {
    success: boolean;
    message: string;
    errorcode: number;
  };
  isSuccess: boolean;
  isFixed: boolean;
  fixedAt: string | null;
  message: string;
}

const ReportingPage: React.FC = () => {
  const intl = useIntl();
  const token = useSelector<any>(state => state?.petrus?.entities?.tokens?.accessToken?.token);
  const [logs, setLogs] = useState<ErrorLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filterOptions, setFilterOptions] = useState<{ [key: string]: string }>({});
  const [selectedLog, setSelectedLog] = useState<ErrorLog | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ type: 'fix' | 'delete'; record: ErrorLog } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const commonHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'Accept-Language': intl.locale,
  };

  const fetchLogs = async (page = 1, pageSize = 10, filters: { [key: string]: string } = {}) => {
    setLoading(true);
    try {
      const activeFilters = Object.entries(filters).filter(([, v]) => v);
      const query = activeFilters.length > 0
        ? `?${activeFilters.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&')}`
        : '';
      const response = await fetch(`${config.api.base}${config.api.reporting}${query}`, {
        method: 'GET',
        headers: commonHeaders,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch error logs');
      }

      const result = await response.json();
      const data: ErrorLog[] = Array.isArray(result) ? result : (result.data || []);
      const total = result.pagination?.total || data.length;

      setLogs(data);
      setPagination({ current: page, pageSize, total });
    } catch (error) {
      message.error(intl.formatMessage({ id: 'reporting.error', defaultMessage: 'Failed to load error logs' }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleFilterChange = (changed: { [key: string]: string }) => {
    setFilterOptions(prev => ({ ...prev, ...changed }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchLogs(1, pagination.pageSize, filterOptions);
  };

  const handleReset = () => {
    setFilterOptions({});
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchLogs(1, pagination.pageSize, {});
  };

  const handleTableChange = (newPagination: any) => {
    const { current, pageSize } = newPagination;
    fetchLogs(current, pageSize, filterOptions);
  };

  const handleMarkFixed = async (record: ErrorLog) => {
    setActionLoading(true);
    try {
      const response = await fetch(
        `${config.api.base}${config.api.reportingLogs}/${record._id}/fix`,
        { method: 'PATCH', headers: commonHeaders },
      );
      if (!response.ok) throw new Error();
      message.success(intl.formatMessage({ id: 'reporting.markFixed.success' }));
      fetchLogs(pagination.current, pagination.pageSize, filterOptions);
    } catch {
      message.error(intl.formatMessage({ id: 'reporting.markFixed.error' }));
    } finally {
      setActionLoading(false);
      setConfirmModal(null);
    }
  };

  const handleDelete = async (record: ErrorLog) => {
    setActionLoading(true);
    try {
      const response = await fetch(
        `${config.api.base}${config.api.reportingLogs}/${record._id}`,
        { method: 'DELETE', headers: commonHeaders },
      );
      if (!response.ok) throw new Error();
      message.success(intl.formatMessage({ id: 'reporting.delete.success' }));
      fetchLogs(pagination.current, pagination.pageSize, filterOptions);
    } catch {
      message.error(intl.formatMessage({ id: 'reporting.delete.error' }));
    } finally {
      setActionLoading(false);
      setConfirmModal(null);
    }
  };

  const columns: ColumnsType<ErrorLog> = [
    {
      title: intl.formatMessage({ id: 'reporting.table.documentName' }),
      dataIndex: 'documentName',
      key: 'documentName',
      width: 180,
      render: (text: string) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text || '-'}</div>
      ),
    },
    {
      title: intl.formatMessage({ id: 'reporting.table.fileName' }),
      dataIndex: 'fileName',
      key: 'fileName',
      width: 180,
      render: (text: string | null) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text || '-'}</div>
      ),
    },
    {
      title: intl.formatMessage({ id: 'reporting.table.messageAi' }),
      dataIndex: 'messageAi',
      key: 'messageAi',
      width: 220,
      render: (text: string | null) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text || '-'}</div>
      ),
    },
    {
      title: intl.formatMessage({ id: 'reporting.table.status' }),
      dataIndex: 'isSuccess',
      key: 'isSuccess',
      width: 100,
      render: (isSuccess: boolean) => (
        <Tag color={isSuccess ? 'green' : 'red'}>
          {isSuccess
            ? intl.formatMessage({ id: 'reporting.status.success' })
            : intl.formatMessage({ id: 'reporting.status.error' })}
        </Tag>
      ),
    },
    {
      title: intl.formatMessage({ id: 'reporting.table.timestamp' }),
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      render: (date: string) => (date ? new Date(date).toLocaleString() : '-'),
      sorter: (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: intl.formatMessage({ id: 'reporting.table.isFixed' }),
      dataIndex: 'isFixed',
      key: 'isFixed',
      width: 100,
      render: (isFixed: boolean) => (
        <Tag color={isFixed ? 'green' : 'orange'}>
          {isFixed
            ? intl.formatMessage({ id: 'reporting.status.fixed' })
            : intl.formatMessage({ id: 'reporting.status.notFixed' })}
        </Tag>
      ),
    },
    {
      title: intl.formatMessage({ id: 'reporting.table.view.full' }),
      key: 'view',
      width: 80,
      render: (_, record) => (
        <Button type="link" onClick={() => setSelectedLog(record)}>
          {intl.formatMessage({ id: 'reporting.action.view' })}
        </Button>
      ),
    },
    {
      title: intl.formatMessage({ id: 'reporting.table.options' }),
      key: 'options',
      width: 60,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'fix',
                label: intl.formatMessage({ id: 'reporting.action.markFixed' }),
                disabled: record.isFixed,
                onClick: () => setConfirmModal({ type: 'fix', record }),
              },
              {
                key: 'delete',
                danger: true,
                label: intl.formatMessage({ id: 'reporting.action.delete' }),
                onClick: () => setConfirmModal({ type: 'delete', record }),
              },
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <div>
        <SeoHead title={intl.formatMessage({ id: 'page.reporting.title' })} />
      </div>
      <div style={{ padding: '24px' }}>
        <div>
          <h1>{intl.formatMessage({ id: 'page.reporting.title' })}</h1>
          <p>{intl.formatMessage({ id: 'page.reporting.description' })}</p>
        </div>
        <Divider />
        <TableSearch
          fields={[
            {
              label: <FormattedMessage id="reporting.table.documentName" />,
              labelFor: 'documentName',
              renderer: (
                <Input
                  id="documentName"
                  value={filterOptions['documentName']}
                  placeholder={intl.formatMessage({ id: 'table.search.placeholder' })}
                  onChange={({ target }) => handleFilterChange({ [target.id]: target.value })}
                />
              ),
            },
            {
              label: <FormattedMessage id="reporting.table.fileName" />,
              labelFor: 'fileName',
              renderer: (
                <Input
                  id="fileName"
                  value={filterOptions['fileName']}
                  placeholder={intl.formatMessage({ id: 'table.search.placeholder' })}
                  onChange={({ target }) => handleFilterChange({ [target.id]: target.value })}
                />
              ),
            },
          ]}
          handleSubmit={handleSubmit}
          handleReset={handleReset}
          rowGutter={[16, 16]}
          colSizes={{ sm: 24, md: 12, lg: 8, xxl: 3 }}
        />
        <Table
          columns={columns}
          dataSource={logs}
          loading={loading}
          rowKey={record => record._id}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
        />
      </div>

      {/* View error modal */}
      <Modal
        title={selectedLog?.message || intl.formatMessage({ id: 'reporting.table.errorMessage' })}
        visible={!!selectedLog}
        onCancel={() => setSelectedLog(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedLog(null)}>
            {intl.formatMessage({ id: 'reporting.modal.close' })}
          </Button>,
        ]}
        width={700}
      >
        <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {selectedLog?.message || '-'}
        </p>
      </Modal>

      {/* Confirm action modal */}
      <Modal
        title={confirmModal
          ? intl.formatMessage({ id: confirmModal.type === 'fix' ? 'reporting.confirm.markFixed.title' : 'reporting.confirm.delete.title' })
          : ''}
        visible={!!confirmModal}
        onCancel={() => setConfirmModal(null)}
        footer={[
          <Button key="cancel" onClick={() => setConfirmModal(null)}>
            {intl.formatMessage({ id: 'reporting.modal.close' })}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger={confirmModal?.type === 'delete'}
            loading={actionLoading}
            onClick={() => {
              if (confirmModal?.type === 'fix') handleMarkFixed(confirmModal.record);
              else if (confirmModal?.type === 'delete') handleDelete(confirmModal.record);
            }}
          >
            {confirmModal
              ? intl.formatMessage({ id: confirmModal.type === 'fix' ? 'reporting.action.markFixed' : 'reporting.action.delete' })
              : ''}
          </Button>,
        ]}
      >
        <p>
          {confirmModal
            ? intl.formatMessage({ id: confirmModal.type === 'fix' ? 'reporting.confirm.markFixed.content' : 'reporting.confirm.delete.content' })
            : ''}
        </p>
      </Modal>
    </>
  );
};

export { ReportingPage };
